/**
 * useAPIManager.ts - React Hook for API Manager with Orchestrators
 * 
 * CHANGE HISTORY:
 * - 251102_1619 danielbeer: Create hook for APIManagerService
 * 
 * Provides easy access to APIManagerService in React components
 */

import { useState, useEffect, useCallback } from 'react';
import { APIManagerService, APIManagerConfig } from './APIManagerService';
import type { APIRequest, APIResponse, APICollection } from './APIManager';

export interface UseAPIManagerOptions {
  actr: any;
  authr?: any;
  aggregatr?: any;
  configgr?: any;
  config: APIManagerConfig;
}

export interface UseAPIManagerReturn {
  service: APIManagerService;
  collections: APICollection[];
  history: APIRequest[];
  credentials: Array<{ id: string; name: string; type: string }>;
  currentEnvironment: { id: string; name: string; variables: Record<string, string> } | null;
  
  // Actions
  executeRequest: (request: APIRequest) => Promise<APIResponse>;
  saveRequest: (request: Omit<APIRequest, 'id'>) => Promise<APIRequest>;
  updateRequest: (id: string, updates: Partial<APIRequest>) => Promise<APIRequest>;
  deleteRequest: (id: string) => Promise<void>;
  
  createCollection: (name: string) => Promise<APICollection>;
  updateCollection: (id: string, updates: Partial<APICollection>) => Promise<APICollection>;
  deleteCollection: (id: string) => Promise<void>;
  
  saveCredential: (credential: any) => Promise<any>;
  deleteCredential: (id: string) => Promise<void>;
  
  switchEnvironment: (id: string) => Promise<void>;
  
  refreshCollections: () => Promise<void>;
  refreshHistory: () => Promise<void>;
  refreshCredentials: () => Promise<void>;
  
  loading: boolean;
  error: string | null;
}

export function useAPIManager(options: UseAPIManagerOptions): UseAPIManagerReturn {
  const [service] = useState(() => new APIManagerService(options));
  const [collections, setCollections] = useState<APICollection[]>([]);
  const [history, setHistory] = useState<APIRequest[]>([]);
  const [credentials, setCredentials] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [currentEnvironment, setCurrentEnvironment] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        refreshCollections(),
        refreshHistory(),
        refreshCredentials()
      ]);
      
      const env = service.getCurrentEnvironment();
      setCurrentEnvironment(env);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshCollections = useCallback(async () => {
    try {
      const cols = await service.getCollections();
      setCollections(cols);
    } catch (err: any) {
      setError(err.message);
    }
  }, [service]);

  const refreshHistory = useCallback(async () => {
    try {
      const hist = await service.getHistory();
      setHistory(hist);
    } catch (err: any) {
      setError(err.message);
    }
  }, [service]);

  const refreshCredentials = useCallback(async () => {
    try {
      const creds = await service.listCredentials();
      setCredentials(creds as any);
    } catch (err: any) {
      setError(err.message);
    }
  }, [service]);

  const executeRequest = useCallback(async (request: APIRequest): Promise<APIResponse> => {
    setLoading(true);
    setError(null);
    try {
      const response = await service.executeRequest(request);
      await refreshHistory(); // Refresh history after execution
      return response;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshHistory]);

  const saveRequest = useCallback(async (request: Omit<APIRequest, 'id'>): Promise<APIRequest> => {
    setLoading(true);
    setError(null);
    try {
      const saved = await service.createRequest(request);
      await refreshCollections();
      return saved;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCollections]);

  const updateRequest = useCallback(async (id: string, updates: Partial<APIRequest>): Promise<APIRequest> => {
    setLoading(true);
    setError(null);
    try {
      const updated = await service.updateRequest(id, updates);
      await refreshCollections();
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCollections]);

  const deleteRequest = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await service.deleteRequest(id);
      await refreshCollections();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCollections]);

  const createCollection = useCallback(async (name: string): Promise<APICollection> => {
    setLoading(true);
    setError(null);
    try {
      const collection = await service.createCollection(name);
      await refreshCollections();
      return collection;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCollections]);

  const updateCollection = useCallback(async (id: string, updates: Partial<APICollection>): Promise<APICollection> => {
    setLoading(true);
    setError(null);
    try {
      const updated = await service.updateCollection(id, updates);
      await refreshCollections();
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCollections]);

  const deleteCollection = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await service.deleteCollection(id);
      await refreshCollections();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCollections]);

  const saveCredential = useCallback(async (credential: any): Promise<any> => {
    setLoading(true);
    setError(null);
    try {
      const saved = await service.saveCredential(credential);
      await refreshCredentials();
      return saved;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCredentials]);

  const deleteCredential = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await service.deleteCredential(id);
      await refreshCredentials();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service, refreshCredentials]);

  const switchEnvironment = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await service.switchEnvironment(id);
      const env = service.getCurrentEnvironment();
      setCurrentEnvironment(env);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [service]);

  return {
    service,
    collections,
    history,
    credentials,
    currentEnvironment,
    
    executeRequest,
    saveRequest,
    updateRequest,
    deleteRequest,
    
    createCollection,
    updateCollection,
    deleteCollection,
    
    saveCredential,
    deleteCredential,
    
    switchEnvironment,
    
    refreshCollections,
    refreshHistory,
    refreshCredentials,
    
    loading,
    error
  };
}

export default useAPIManager;
